import { useAssertedActiveTabId } from 'hooks/use-asserted-active-tab-id';
import { useAppSelector } from 'hooks/useAppSelector';
import { ISalesCounterpart } from 'models/ISalesCounterpart';
import { useMemo } from 'react';
import { ClientCard } from 'routes/smart-quote/components/prompt-results/components/client-card';
import { PromptTableSections } from 'routes/smart-quote/components/prompt-results/prompt-table-sections';
import ResultsSummary from 'routes/smart-quote/components/prompt-results/results-summary';
import { UnsupportedProductPrompt } from 'routes/smart-quote/components/prompt-results/unsupported-product';
import { useSendPrompt } from 'routes/smart-quote/hooks/use-send-prompt';
import { trackSmartQuoteEvent } from 'routes/smart-quote/utils';
import { getPrompt } from 'state/workspaces/smart-quote/selectors';

type PromptResultsProps = {
  promptId: string;
  pageRef: React.RefObject<HTMLDivElement | null>;
};

const PromptResults = ({ promptId, pageRef }: PromptResultsProps) => {
  const tabId = useAssertedActiveTabId();
  const prompt = useAppSelector(state => getPrompt(state, tabId, promptId));
  const { handlePromptSubmit } = useSendPrompt(pageRef);

  const unsupportedVariations = useMemo(
    () =>
      Object.values(prompt?.results ?? {})
        .filter(result => result.error?.errorCode === 'UnsupportedPayoff')
        .map(result => result.productKey),
    [prompt?.results],
  );

  const isInitRequest = prompt.status === 'Initialized';
  const hasUnsupportedProducts = unsupportedVariations.length > 0;
  const shouldCheckClientSelected = !!prompt.pendingFile;

  if (isInitRequest) {
    return null;
  }

  const handleResumeParseEmail = async (client?: ISalesCounterpart) => {
    if (client) {
      trackSmartQuoteEvent('select client', 'from prompt');
    }
    const blob = await fetch(prompt.pendingFile!).then(res => res.blob());
    const file = new File([blob], prompt.filename);
    handlePromptSubmit({ email: file, client });
    trackSmartQuoteEvent('request quote', 'prompt client');
  };

  return (
    <div className="d-flex flex-column py-3 pb-2 gap-2">
      {hasUnsupportedProducts && <UnsupportedProductPrompt products={unsupportedVariations} />}
      {shouldCheckClientSelected ? (
        <ClientCard onClientSelected={handleResumeParseEmail} onSkipClientSelection={handleResumeParseEmail} />
      ) : (
        <>
          <ResultsSummary prompt={prompt} />
          <PromptTableSections promptId={promptId} />
        </>
      )}
    </div>
  );
};

export default PromptResults;
